/**
 * Direct access to where `@convex-dev/auth` keeps its credential.
 *
 * The library persists its JWT in localStorage under
 * `__convexAuthJWT_<namespace>` and its refresh token under
 * `__convexAuthRefreshToken_<namespace>`, where the namespace defaults to the
 * Convex deployment URL with every non-alphanumeric character stripped (see
 * `useNamespacedStorage` in `@convex-dev/auth/react`). Both helpers below
 * track that scheme, so it is written down in exactly one place. If the
 * library ever changes it, the hint reads false (costing only the slower,
 * server-verified path) and the reset becomes a no-op (costing the recovery
 * in `AuthBridge`, which is already a last resort).
 */
const storageKeys = (): { jwt: string; refresh: string } | null => {
  if (typeof window === 'undefined') {
    return null
  }
  const convexUrl = import.meta.env.VITE_CONVEX_URL
  if (!convexUrl) {
    return null
  }
  const namespace = convexUrl.replace(/[^a-zA-Z0-9]/g, '')
  return {
    jwt: `__convexAuthJWT_${namespace}`,
    refresh: `__convexAuthRefreshToken_${namespace}`,
  }
}

/**
 * Local, synchronous hint that this browser has a signed-in Convex Auth
 * session — without touching the network or waiting for the auth machinery
 * to boot.
 *
 * This is a HINT, not an auth check: a stored token may be expired. Callers
 * must only use it for optimistic routing (e.g. marketing → app shell) and
 * let the real gate on the destination route verify the session.
 */
export const hasStoredAuthTokenHint = (): boolean => {
  const keys = storageKeys()
  if (keys === null) {
    return false
  }
  try {
    return window.localStorage.getItem(keys.jwt) !== null
  } catch {
    // Storage access can throw (e.g. blocked third-party contexts) — treat
    // as signed out and fall through to the server-verified path.
    return false
  }
}

/**
 * Whether this document has just returned from an OAuth provider and still
 * needs `ConvexAuthProvider` to exchange the one-time callback code for a
 * stored session.
 *
 * This deliberately mirrors the provider's own initial URL check. The code
 * is not proof of authentication; it only lets the authenticated route mount
 * long enough for the provider to verify it. Queries remain gated on
 * `useConvexAuth().isAuthenticated`, so the hint itself grants no access.
 */
export const hasAuthCallbackCode = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  return Boolean(new URLSearchParams(window.location.search).get('code'))
}

/**
 * Synchronous routing hint for entering the authenticated shell.
 *
 * Usually the stored JWT is enough. On the first render after Google OAuth,
 * however, there is no JWT yet: only `?code=...`. Redirecting before the auth
 * provider mounts would discard that code and send a successful sign-in back
 * to the login screen.
 */
export const hasPendingOrStoredAuth = (): boolean =>
  hasStoredAuthTokenHint() || hasAuthCallbackCode()

/**
 * Drops the stored credential without going through `signOut()`.
 *
 * Only for recovering a session that can no longer settle — see the watchdog
 * in `AuthBridge`. `signOut()` is the right call everywhere else, because it
 * also revokes the session server-side; this does not, and leaves the row to
 * expire on its own.
 *
 * It exists because `signOut()` cannot be used on that path: it awaits
 * `client.action('auth:signOut')` over the WebSocket, and the failure being
 * recovered from is one where the client has stopped that socket and will
 * never restart it. The call hangs inside the library's own try block, so the
 * token erasure that follows it never runs. Clearing storage directly is the
 * only step that cannot deadlock.
 */
export const clearStoredAuthTokens = (): void => {
  const keys = storageKeys()
  if (keys === null) {
    return
  }
  try {
    window.localStorage.removeItem(keys.jwt)
    window.localStorage.removeItem(keys.refresh)
  } catch {
    // Storage access can throw; the reload that follows will simply meet the
    // same wedged state and the recovery latch stops it from looping.
  }
}
